import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of, throwError } from 'rxjs';
import { User } from '../../model/User';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
// `Organization` was imported only for the old `resendEmailInvite` return
// type, which was wrong anyway - that route returns no Organization.
import {DataService} from "./data-service";

/**
 * Response shape of `POST /user/repairaccount`.
 *
 * `repaired: false` is a SUCCESS - it means the account was already set up
 * correctly. It is not a no-op that needs different handling.
 */
export interface RepairAccountResponse {
  repaired: boolean;
  roleId: number;
  role: string;
}

/**
 * Response shape of `GET /user/{userId}/invitestatus`.
 *
 * The status lives in Cognito, not the database, so it is deliberately NOT on
 * the `User` row: inlining it would cost one `AdminGetUser` per user per list
 * render. Confirmed in
 * docs/contracts/inbox/_processed/2026-09-02-resend-invite-was-destructive-now-fixed.md
 */
export interface InviteStatusResponse {
  status: string;
  data: {
    user_ID: number;
    /**
     * The raw Cognito enum -- `FORCE_CHANGE_PASSWORD`, `CONFIRMED`,
     * `RESET_REQUIRED`, `UNCONFIRMED`, `EXTERNAL_PROVIDER`, ... -- or `null`
     * when no Cognito account exists for the row.
     */
    userStatus: string | null;
    /** The coarse boolean. True ONLY for `FORCE_CHANGE_PASSWORD`. */
    invitePending: boolean;
  };
}

@Injectable()
export class UserService extends DataService {
  private objects: User[] = [];
  private objectsObservable = of( this.objects );
  public loadingState = 'INITIAL';

  constructor(private http: HttpClient, private messageService: MessageService, private startupService: StartupService) {
    super(startupService);
    // this.startupService.setUserService(this);
  }

    reset (filter: string = '') {
    this.loadingState = 'INITIAL';
    while ( this.objects.length > 0) {
      this.objects.pop();
    }
  }

  /**
   * POST: recover an account that signed in successfully but has no role
   * behind it - where a migrating legacy user lands when the Cognito
   * PostConfirmation trigger did not complete.
   *
   * Takes no body and no id. The route is deliberately role-free server-side,
   * reading only the validated token, because every other `user` route
   * resolves the caller through `custom:roleId` - the exact attribute this
   * caller is missing.
   *
   * Idempotent, so callers do NOT check first. It resolves an existing
   * User/Organization pair before creating one, so repeated calls converge on
   * a single `roleId` rather than minting a new Organization each time.
   *
   * Fails loudly. A repair that silently did not happen leaves the user on
   * the same dead screen with no way to tell the difference.
   *
   * The caller MUST obtain a fresh token afterwards. `custom:roleId` is
   * written to the Cognito user and the token in hand predates it, so
   * re-reading the current claims would report a successful repair as a
   * failure. See `StartupService.refreshJwtToken()`.
   */
  repairAccount (): Observable<RepairAccountResponse> {
    const url = `${environment.apiBaseURL}user/repairaccount`;
    this.messageService.add('UserService: repairAccount');
    return this.http.post<RepairAccountResponse>(url, {}, this.startupService.getHttpOptions()).pipe(
      tap(r => this.log(`repairAccount repaired=${r && r.repaired} role=${r && r.role} roleId=${r && r.roleId}`)),
      catchError(this.failLoudly<RepairAccountResponse>('repairAccount'))
    );
  }

  /**
   * PUT: ask the server to send the Cognito invitation email again.
   *
   * **This button used to destroy accounts.** Until 2026-09-02 the server's
   * handler called `createCognitoUser(..., resend = true)`, which begins with
   * `adminDeleteUser(email)` -- so "Resend Email Invite" deleted the user's
   * Cognito account and made a new one, taking their password, `sub`, MFA and
   * device registrations with it. `AdminDeleteUser` is eventually consistent,
   * so the immediately-following create sometimes lost the race and returned
   * `UsernameExistsException` -- the intermittent failure we reported. Worse,
   * the success path then threw on a non-existent `Repository.one()`, so the
   * operator saw an error and clicked again, destroying the account a second
   * time. Full account in
   * docs/contracts/inbox/_processed/2026-09-02-resend-invite-was-destructive-now-fixed.md
   *
   * It is now a real `AdminCreateUser` with `MessageAction: RESEND`, and the
   * server refuses unless the account is `FORCE_CHANGE_PASSWORD`, so a
   * CONFIRMED user cannot be touched by this route whatever the client does.
   *
   * **No body.** The server confirmed the handler reads only `req.params.userId`
   * and never merges `req.body`, so the whole `User` row we used to send was
   * inert -- and sending it risked exactly the stale-republish class that
   * `SAVE_ALLOWED_FIELDS` was narrowed for on Projects, on a route where it
   * could have reached `isAdmin` and `active`. Do not put it back.
   *
   * Fails loudly: a refusal must reach the operator rather than reporting an
   * invitation that was never sent. Callers branch on `data.code`
   * (`NOT_FOUND`, `NO_COGNITO_USER`, `NOT_INVITE_PENDING`).
   */
  resendEmailInvite (user: User): Observable<any> {
    const url = `${environment.apiBaseURL}user/resendEmailInvite/${user.ID}`;
    this.messageService.add(`UserService: resendEmailInvite id=${user.ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`resendEmailInvite id=${user.ID}`)),
      catchError(this.failLoudly<any>('resendEmailInvite'))
    );
  }

  /**
   * GET: the Cognito-side invite state for one user, used to gate the resend
   * button.
   *
   * One request per user, so call it per row only on screens that show a
   * handful. The server has offered a batch form built on `ListUsers` if a
   * list view proves too chatty -- ask for it rather than firing N of these.
   *
   * Does NOT fail loudly. An unreadable status is a reason to leave the button
   * hidden, not a reason to put a toast in front of an operator who was only
   * looking at a list.
   */
  getInviteStatus (user: User): Observable<InviteStatusResponse> {
    const url = `${environment.apiBaseURL}user/${user.ID}/invitestatus`;
    this.messageService.add(`UserService: getInviteStatus id=${user.ID}`);
    return this.http.get<InviteStatusResponse>(url, this.startupService.getHttpOptions()).pipe(
      tap(r => this.log(`getInviteStatus id=${user.ID} invitePending=${r && r.data && r.data.invitePending}`)),
      catchError(this.handleError<InviteStatusResponse>('getInviteStatus', undefined))
    );
  }

  /**
   * Error path for operations that change what a user can GET INTO.
   *
   * The difference from `handleError` matters and is the whole point:
   * `handleError` returns `of(result)`, and an observable that emits is
   * indistinguishable from success to the subscriber -- so
   * `subscribe(x => user.isAdmin = 'TRUE')` ran on failure too, and the screen
   * confirmed a privilege change that never happened. For a *read* that is a
   * reasonable degradation (show nothing, keep the app alive). For granting or
   * revoking access it is the worst possible behaviour: an operator who
   * believes they have cut somebody's access, and has not, will not check
   * again.
   *
   * So these rethrow. Every caller must have an error handler that puts the
   * failure in front of the user -- see `ServerErrorToastService`.
   *
   * NOTE this only helps once the server actually reports the failure. The
   * server currently resolves Cognito group errors quietly inside
   * `addUserToOrganizationsGroups` / `removeUserFromOrganizationsGroups` and
   * still returns 200; that half is requested in
   * outbox/2026-08-27-decisions-dedup-cognito-errors-and-focusorg.md. Until it
   * ships nothing here changes behaviour, which is the correct ordering: the
   * client is ready to report a failure before the server starts sending one.
   */
  private failLoudly<T> (operation: string) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed`, error);
      this.log(`${operation} failed: ${error && error.message ? error.message : error}`);
      return throwError(() => error);
    };
  }

  getUsers(): Observable<User[]> {
    if ( this.loadingState === 'INITIAL') {
      this.loadingState = 'LOADING';
      const url = `${environment.apiBaseURL}user`;
      this.messageService.add('UserService: fetched users');
      return this.http.get<User[]>(url, this.startupService.getHttpOptions())
        .pipe(
          map(users => {
            while ( this.objects.length > 0) {
              this.objects.pop();
            }
            for ( const user of users ) {
              const o = new User(user);
              this.objects.push(o);
            }
            this.loadingState = 'LOADED';
            return this.objects;
          }),
          catchError(this.handleError('getUsers', []))
        );
    } else {
      return this.objectsObservable;
    }
  }

  getUser(id: string): Observable<User> {
    const url = `${environment.apiBaseURL}user/${id}`;
    this.messageService.add(`UserService: fetched user id=${id}`);
    return this.http.get<User>(url, this.startupService.getHttpOptions())
      .pipe(
        map(user => {
          const p = new User(user);
          return p;
        }),
        catchError(this.handleError<User>(`getUser id=${id}`))
      );
  }

  /** PUT: update the user on the server */
  updateUser (user: User): Observable<User> {
    const url = `${environment.apiBaseURL}user/${user.ID}`;
    this.messageService.add(`UserService: updated user id=${user.ID}`);
    return this.http.put(url, user, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`updated user id=${user.ID}`)),
      catchError(this.handleError<any>('updateUser'))
    );
  }
  /** PUT: update the user on the server */
  enable (user: User): Observable<User> {
    const url = `${environment.apiBaseURL}user/enable/${user.ID}`;
    this.messageService.add(`UserService: disabled user id=${user.ID}`);
    return this.http.put(url, user, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`updated user id=${user.ID}`)),
      catchError(this.failLoudly<any>('enableUser'))
    );
  }
  /** PUT: update the user on the server */
  disable (user: User): Observable<User> {
    const url = `${environment.apiBaseURL}user/disable/${user.ID}`;
    this.messageService.add(`UserService: disabled user id=${user.ID}`);
    return this.http.put(url, user, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`updated user id=${user.ID}`)),
      // Access change (the server calls Cognito adminDisableUser), so it fails
      // loudly like the grant/revoke pair rather than reporting a disable that
      // did not happen.
      catchError(this.failLoudly<any>('disableUser'))
    );
  }

  addUser (user: User): Observable<User> {
    const url = `${environment.apiBaseURL}user`;
    return this.http.post<User>(url, user, this.startupService.getHttpOptions()).pipe(
      tap(o => {
        this.log(`added user w/ email=${user.email}`);
        this.objects.push(new User(o));
      }),
      catchError(this.failLoudly<User>('addUser'))
    );
  }

  /** DELETE: delete the user from the server */
  deleteUser (user: User): Observable<User> {
    const url = `${environment.apiBaseURL}user/${user.ID}`;

    return this.http.delete<User>(url, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`deleted user id=${user.ID}`)),
      catchError(this.handleError<User>('deleteUser'))
    );
  }

  /** PUT: set a user to be an admin of an organizatoin */
  grantProperty (user: User, property: string, value: string): Observable<User> {
    const userId = user.ID;
    const url = `${environment.apiBaseURL}user/grantProperty/${userId}/${property}/${value}`;
    this.messageService.add(`UserService: grantProperty user id=${userId} property=${property} value=${value}`);
    return this.http.put(url, user, this.startupService.getHttpOptions()).pipe(
      tap(x => {
        this.log(`grantProperty user id=${userId} property=${property} value=${value}`);
      }),
      catchError(this.failLoudly<any>('grantProperty'))
    );
  }

  /** PUT: set a user to be an admin of an organizatoin */
  revokeProperty (user: User, property: string, value: string): Observable<User> {
    const userId = user.ID;
    const url = `${environment.apiBaseURL}user/revokeProperty/${userId}/${property}/${value}`;
    this.messageService.add(`UserService: revokeProperty user id=${userId} property=${property} value=${value}`);
    return this.http.put(url, user, this.startupService.getHttpOptions()).pipe(
      tap(x => {
        this.log(`revokeProperty userId=${userId} property=${property} value=${value}`);
      }),
      catchError(this.failLoudly<any>(`revokeProperty userId${userId} property=${property} value=${value}`))
    );
  }



  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  private log(message: string) {
    this.messageService.add('UserService: ' + message);
  }

}
